import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowLeft, Plus, Trash2, Upload, Save, GripVertical,
  CheckCircle, Type, List, MessageSquare, Cloud, BarChart2,
  Loader2, X, FileText, Copy, Vote, Star, Image, Smile
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice', icon: List },
  { value: 'true_false', label: 'True / False', icon: CheckCircle },
  { value: 'poll', label: 'Poll (Single)', icon: BarChart2 },
  { value: 'poll_multi', label: 'Poll (Multi)', icon: Vote },
  { value: 'rating', label: 'Rating Scale', icon: Star },
  { value: 'open_ended', label: 'Open Ended', icon: MessageSquare },
  { value: 'word_cloud', label: 'Word Cloud', icon: Cloud },
  { value: 'emoji_reaction', label: 'Emoji Reaction', icon: Smile },
  { value: 'ranking', label: 'Ranking', icon: BarChart2 },
];

export default function QuizEditor({ quizId, onBack }) {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => { loadQuiz(); }, [quizId]);

  const loadQuiz = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quiz/${quizId}`);
      setQuiz(res.data);
    } catch (err) {
      alert('Failed to load quiz');
      onBack && onBack();
    } finally {
      setLoading(false);
    }
  };

  const saveQuiz = async () => {
    if (!quiz) return;
    setSaving(true);
    try {
      await axios.put(`${API}/quiz/${quizId}`, {
        title: quiz.title,
        description: quiz.description,
        mode: quiz.mode,
        questions: quiz.questions,
        team_mode: quiz.team_mode,
        teams: quiz.teams,
        default_time_limit: quiz.default_time_limit,
      });
    } catch (err) {
      alert('Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = (type = 'multiple_choice') => {
    const defaults = {
      multiple_choice: { options: ['', '', '', ''], correct_answer: '' },
      true_false: { options: ['True', 'False'], correct_answer: 'True' },
      poll: { options: ['', '', '', ''], correct_answer: '', show_results_live: true },
      poll_multi: { options: ['', '', '', ''], correct_answer: '', show_results_live: true },
      rating: { options: [], correct_answer: '' },
      open_ended: { options: [], correct_answer: '' },
      word_cloud: { options: [], correct_answer: '' },
      emoji_reaction: { options: ['👍', '❤️', '😂', '😮', '🙏', '🔥'], correct_answer: '' },
      ranking: { options: ['', '', ''], correct_answer: '' },
    };
    const d = defaults[type] || defaults.multiple_choice;
    const newQ = { id: crypto.randomUUID(), type, text: '', image_url: '', ...d };
    setQuiz({ ...quiz, questions: [...(quiz.questions || []), newQ] });
  };

  const updateQuestion = (index, updates) => {
    const questions = [...quiz.questions];
    questions[index] = { ...questions[index], ...updates };
    setQuiz({ ...quiz, questions });
  };

  const removeQuestion = (index) => {
    const questions = quiz.questions.filter((_, i) => i !== index);
    setQuiz({ ...quiz, questions });
  };

  const updateOption = (qIndex, oIndex, value) => {
    const questions = [...quiz.questions];
    const options = [...questions[qIndex].options];
    options[oIndex] = value;
    questions[qIndex] = { ...questions[qIndex], options };
    setQuiz({ ...quiz, questions });
  };

  const addOption = (qIndex) => {
    const questions = [...quiz.questions];
    questions[qIndex] = { ...questions[qIndex], options: [...questions[qIndex].options, ''] };
    setQuiz({ ...quiz, questions });
  };

  const removeOption = (qIndex, oIndex) => {
    const questions = [...quiz.questions];
    const options = questions[qIndex].options.filter((_, i) => i !== oIndex);
    questions[qIndex] = { ...questions[qIndex], options };
    setQuiz({ ...quiz, questions });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File too large. Max 10MB.');
      return;
    }

    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/quiz/${quizId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult({ success: true, message: res.data.message });
      loadQuiz(); // Reload to get new questions
    } catch (err) {
      setUploadResult({ success: false, message: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#1B3A6B]" />
        <p className="text-[#475569]">Loading quiz...</p>
      </div>
    );
  }

  if (!quiz) return null;

  return (
    <div data-testid="quiz-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button onClick={onBack} className="p-2 hover:bg-[#F4F2EC] rounded-lg transition-colors flex-shrink-0" data-testid="quiz-editor-back">
            <ArrowLeft className="w-5 h-5 text-[#475569]" />
          </button>
          <div className="min-w-0 flex-1">
            <input
              type="text"
              value={quiz.title}
              onChange={e => setQuiz({ ...quiz, title: e.target.value })}
              className="text-lg sm:text-xl font-bold text-[#0F1A2A] bg-transparent border-none focus:ring-0 p-0 w-full"
              placeholder="Quiz title"
              data-testid="quiz-editor-title"
            />
            <input
              type="text"
              value={quiz.description || ''}
              onChange={e => setQuiz({ ...quiz, description: e.target.value })}
              className="text-xs sm:text-sm text-[#475569] bg-transparent border-none focus:ring-0 p-0 w-full mt-0.5"
              placeholder="Add a description..."
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={quiz.mode}
            onChange={e => setQuiz({ ...quiz, mode: e.target.value })}
            className="text-xs sm:text-sm px-2 sm:px-3 py-2 border border-[#E2DFD8] rounded-lg"
          >
            <option value="admin_controlled">Live</option>
            <option value="self_paced">Self-paced</option>
          </select>
          <button onClick={saveQuiz} disabled={saving} className="btn-primary py-2 px-3 sm:px-4 rounded-lg font-medium flex items-center gap-1.5 text-xs sm:text-sm disabled:opacity-50" data-testid="quiz-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>

      {/* Timer & Team Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-3 sm:p-5 mb-4 sm:mb-6">
        <h4 className="font-semibold text-[#0F1A2A] mb-3 text-sm sm:text-base">Quiz Settings</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="block text-sm font-medium text-[#475569] mb-1">Default Time Limit (seconds)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={300}
                value={quiz.default_time_limit || 0}
                onChange={e => setQuiz({ ...quiz, default_time_limit: parseInt(e.target.value) || 0 })}
                className="w-24 px-3 py-2 border border-[#E2DFD8] rounded-lg text-sm focus:ring-2 focus:ring-[#1B3A6B]"
                data-testid="default-time-limit"
              />
              <span className="text-xs text-[#94A3B8]">0 = no timer</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#475569] mb-1">Team Mode</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuiz({ ...quiz, team_mode: !quiz.team_mode })}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${quiz.team_mode ? 'bg-[#1B3A6B] text-white border-[#1B3A6B]' : 'bg-white text-[#475569] border-[#E2DFD8] hover:border-[#1B3A6B]'}`}
                data-testid="toggle-team-mode"
              >
                {quiz.team_mode ? 'Teams: ON' : 'Teams: OFF'}
              </button>
            </div>
          </div>
        </div>

        {/* Team Editor */}
        {quiz.team_mode && (
          <div className="mt-4 pt-4 border-t border-[#E2DFD8]">
            <label className="block text-sm font-medium text-[#475569] mb-2">Teams</label>
            <div className="space-y-2">
              {(quiz.teams || []).map((team, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="color"
                    value={team.color || '#1B3A6B'}
                    onChange={e => {
                      const teams = [...(quiz.teams || [])];
                      teams[i] = { ...teams[i], color: e.target.value };
                      setQuiz({ ...quiz, teams });
                    }}
                    className="w-8 h-8 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={team.name || ''}
                    onChange={e => {
                      const teams = [...(quiz.teams || [])];
                      teams[i] = { ...teams[i], name: e.target.value, id: e.target.value.toLowerCase().replace(/\s+/g, '_') };
                      setQuiz({ ...quiz, teams });
                    }}
                    placeholder="Team name"
                    className="flex-1 px-3 py-1.5 border border-[#E2DFD8] rounded-lg text-sm focus:ring-2 focus:ring-[#1B3A6B]"
                  />
                  <button onClick={() => {
                    const teams = (quiz.teams || []).filter((_, idx) => idx !== i);
                    setQuiz({ ...quiz, teams });
                  }} className="p-1 hover:bg-red-50 rounded">
                    <X className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const colors = ['#1B3A6B', '#B8860B', '#dc2626', '#059669', '#7c3aed', '#ea580c'];
                  const idx = (quiz.teams || []).length;
                  const teams = [...(quiz.teams || []), { id: `team_${idx + 1}`, name: '', color: colors[idx % colors.length] }];
                  setQuiz({ ...quiz, teams });
                }}
                className="text-xs text-[#1B3A6B] hover:underline flex items-center gap-1"
                data-testid="add-team-btn"
              >
                <Plus className="w-3 h-3" /> Add team
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-xl border border-dashed border-[#B8860B] p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-[#B8860B]" />
            <div>
              <p className="text-sm font-medium text-[#0F1A2A]">Upload Questions</p>
              <p className="text-xs text-[#94A3B8]">PDF, Word (.docx), CSV, TXT, or Image (JPEG/PNG)</p>
            </div>
          </div>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf,.docx,.csv,.txt,.jpg,.jpeg,.png,.webp" onChange={handleUpload} className="hidden" data-testid="quiz-upload-input" />
            <span className="px-4 py-2 bg-[#B8860B] text-white text-sm font-medium rounded-lg hover:bg-[#9A7209] transition-colors inline-flex items-center gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Parsing...' : 'Choose File'}
            </span>
          </label>
        </div>
        {uploadResult && (
          <p className={`mt-2 text-sm ${uploadResult.success ? 'text-green-600' : 'text-red-500'}`}>
            {uploadResult.message}
          </p>
        )}
      </div>

      {/* Questions List */}
      <div className="space-y-4 mb-6">
        {(quiz.questions || []).map((q, qIdx) => (
          <div key={q.id} className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-5" data-testid={`question-${qIdx}`}>
            <div className="flex items-start gap-3 mb-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1B3A6B] text-white text-xs font-bold flex items-center justify-center mt-1">
                {qIdx + 1}
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <select
                    value={q.type}
                    onChange={e => updateQuestion(qIdx, { type: e.target.value, options: e.target.value === 'true_false' ? ['True', 'False'] : q.options })}
                    className="text-xs px-2 py-1 border border-[#E2DFD8] rounded-lg bg-[#F9F8F6] font-medium"
                  >
                    {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <button onClick={() => removeQuestion(qIdx)} className="ml-auto p-1 hover:bg-red-50 rounded" title="Remove question">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                <textarea
                  value={q.text}
                  onChange={e => updateQuestion(qIdx, { text: e.target.value })}
                  placeholder="Enter your question..."
                  rows={2}
                  className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent text-sm resize-none"
                />

                {/* Image URL (for any question type) */}
                <div className="mt-2">
                  <input
                    type="url"
                    value={q.image_url || ''}
                    onChange={e => updateQuestion(qIdx, { image_url: e.target.value })}
                    placeholder="Image URL (optional)"
                    className="w-full px-3 py-1.5 border border-[#E2DFD8] rounded-lg text-xs focus:ring-2 focus:ring-[#1B3A6B] text-[#475569]"
                  />
                </div>

                {/* Options for MC, T/F, Ranking, Poll, Poll Multi */}
                {['multiple_choice', 'true_false', 'ranking', 'poll', 'poll_multi'].includes(q.type) && (
                  <div className="mt-3 space-y-2">
                    <label className="text-xs font-medium text-[#475569]">Options:</label>
                    {(q.options || []).map((opt, oIdx) => (
                      <div key={oIdx} className="flex items-center gap-2">
                        <span className="text-xs text-[#94A3B8] w-5">{String.fromCharCode(65 + oIdx)}.</span>
                        <input
                          type="text"
                          value={opt}
                          onChange={e => updateOption(qIdx, oIdx, e.target.value)}
                          placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                          className="flex-1 px-3 py-1.5 border border-[#E2DFD8] rounded-lg text-sm focus:ring-2 focus:ring-[#1B3A6B]"
                          disabled={q.type === 'true_false'}
                        />
                        {q.type !== 'true_false' && (q.options || []).length > 2 && (
                          <button onClick={() => removeOption(qIdx, oIdx)} className="p-1 hover:bg-red-50 rounded">
                            <X className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        )}
                      </div>
                    ))}
                    {!['true_false'].includes(q.type) && (
                      <button onClick={() => addOption(qIdx)} className="text-xs text-[#1B3A6B] hover:underline flex items-center gap-1 mt-1">
                        <Plus className="w-3 h-3" /> Add option
                      </button>
                    )}
                  </div>
                )}

                {/* Emoji options for emoji_reaction */}
                {q.type === 'emoji_reaction' && (
                  <div className="mt-3 space-y-2">
                    <label className="text-xs font-medium text-[#475569]">Emoji Options:</label>
                    <div className="flex flex-wrap gap-2">
                      {(q.options || []).map((emoji, oIdx) => (
                        <div key={oIdx} className="flex items-center gap-1">
                          <input
                            type="text"
                            value={emoji}
                            onChange={e => updateOption(qIdx, oIdx, e.target.value)}
                            className="w-12 text-center text-lg px-1 py-1 border border-[#E2DFD8] rounded-lg"
                            maxLength={2}
                          />
                          <button onClick={() => removeOption(qIdx, oIdx)} className="p-0.5 hover:bg-red-50 rounded">
                            <X className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => addOption(qIdx)} className="text-xs text-[#1B3A6B] hover:underline flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Add emoji
                      </button>
                    </div>
                  </div>
                )}

                {/* Poll: show results live toggle */}
                {['poll', 'poll_multi', 'emoji_reaction', 'rating'].includes(q.type) && (
                  <div className="mt-3 flex items-center gap-3">
                    <label className="text-xs font-medium text-[#475569]">Show results to participants live:</label>
                    <button
                      onClick={() => updateQuestion(qIdx, { show_results_live: !q.show_results_live })}
                      className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${q.show_results_live ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`}
                    >
                      {q.show_results_live ? 'Yes' : 'No'}
                    </button>
                  </div>
                )}

                {/* Correct Answer + Timer for graded types */}
                {(q.type === 'multiple_choice' || q.type === 'true_false') && (
                  <div className="mt-3 flex items-center gap-4">
                    <div>
                      <label className="text-xs font-medium text-[#475569]">Correct Answer:</label>
                      <select
                        value={q.correct_answer || ''}
                        onChange={e => updateQuestion(qIdx, { correct_answer: e.target.value })}
                        className="ml-2 text-sm px-2 py-1 border border-[#E2DFD8] rounded-lg bg-green-50"
                      >
                        <option value="">-- Select --</option>
                        {(q.options || []).map((opt, i) => (
                          <option key={i} value={opt}>{opt || `Option ${String.fromCharCode(65 + i)}`}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#475569]">Timer (s):</label>
                      <input
                        type="number"
                        min={0}
                        max={300}
                        value={q.time_limit || ''}
                        onChange={e => updateQuestion(qIdx, { time_limit: parseInt(e.target.value) || 0 })}
                        placeholder="—"
                        className="ml-2 w-16 text-sm px-2 py-1 border border-[#E2DFD8] rounded-lg"
                        title="Override default timer for this question (0 = use default)"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Question Buttons */}
      <div className="bg-white rounded-xl border border-dashed border-[#E2DFD8] p-3 sm:p-4">
        <p className="text-xs sm:text-sm font-medium text-[#475569] mb-2 sm:mb-3">Add Question:</p>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {QUESTION_TYPES.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                onClick={() => addQuestion(t.value)}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 border border-[#E2DFD8] rounded-lg text-[10px] sm:text-sm text-[#475569] hover:bg-[#F4F2EC] hover:border-[#1B3A6B] transition-colors"
                data-testid={`add-question-${t.value}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom: Join Link + Info */}
      <div className="mt-4 bg-[#1B3A6B] rounded-xl p-4 text-white">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-white/60 uppercase tracking-wider font-medium">Participant Join Link</p>
          <span className="text-xs text-white/60">{(quiz.questions || []).length} question(s)</span>
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-sm bg-white/10 px-3 py-2 rounded-lg truncate font-mono" data-testid="editor-join-link">
            {window.location.origin}/quiz/join/{quiz.join_code}
          </code>
          <button
            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/quiz/join/${quiz.join_code}`); alert('Join link copied!'); }}
            className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors flex-shrink-0"
            data-testid="copy-editor-link"
          >
            <Copy className="w-4 h-4" /> Copy Link
          </button>
        </div>
        <p className="text-xs text-white/50 mt-2">Share this link with participants. Code: <span className="font-mono font-bold text-white/80">{quiz.join_code}</span></p>
      </div>
    </div>
  );
}
